import ApplicationSerializer from '@square/dashboard-data/serializers/application';
import { EmbeddedRecordsMixin } from '@ember-data/serializer/rest';
import { isEmpty, isNone } from '@ember/utils';
import { formatCanadianTaxId, formatCanadianPstRstQstId } from '@square/dashboard-data/utils/business-number-formatters';
import { inject as service } from '@ember/service';
export default ApplicationSerializer.extend(EmbeddedRecordsMixin, {
  userInfo: service(),
  attrs: {
    addressLine3: 'address_line_3',
    merchantImages: {
      embedded: 'always'
    },
    posTerminalImages: {
      embedded: 'always'
    },
    hours: {
      embedded: 'always'
    },
    scriptAddresses: {
      embedded: 'always'
    }
  },
  primaryKey: 'token',
  rootKey: 'entity',
  // The user token needs to be propagated to the embedded merchant_images
  // so that merchant_images can synthesize unique IDs to persist in the store.
  normalize(modelClass, resourceHash) {
    if (resourceHash) {
      const unitToken = resourceHash['token'];
      const merchantImages = resourceHash['merchant_images'];
      if (unitToken && merchantImages) {
        for (const merchantImage of merchantImages) {
          merchantImage['user_token'] = unitToken;
        }
      }
      const posTerminalImages = resourceHash['pos_terminal_images'];
      if (unitToken && posTerminalImages) {
        for (const posTerminalImage of posTerminalImages) {
          posTerminalImage['user_token'] = unitToken;
        }
      }
      const scriptAddresses = resourceHash['script_addresses'];
      if (scriptAddresses) {
        for (const scriptAddress of scriptAddresses) {
          scriptAddress['id'] = `${unitToken}-scriptAddress-${scriptAddress.script}`;
        }
      }
    }
    return this._super(modelClass, resourceHash);
  },
  normalizeResponse(store, primaryModelClass, payload, id, requestType) {
    const dataToLoad = this.rootKey ? payload[this.rootKey] : payload;
    if (dataToLoad && id) {
      dataToLoad.token = id;
    }
    if (dataToLoad && isNone(dataToLoad.iana_time_zone)) {
      // FIXME (forump): Handle DST related edge cases.
      const currentTimezone = new Date(2013, 0, 1).getTimezoneOffset() * 60 * -1;
      const features = this.userInfo.countryFeatures;
      const defaultTimezoneSelection = features.timezoneSelections.findBy('offset', currentTimezone);
      if (defaultTimezoneSelection && defaultTimezoneSelection.value) {
        dataToLoad.iana_time_zone = defaultTimezoneSelection.value;
      } else {
        dataToLoad.iana_time_zone = features.timezoneSelections[0].value;
      }
    }

    // make AU address back compatible
    if (dataToLoad && dataToLoad.country_code === 'AU') {
      if (dataToLoad.street2) {
        if (dataToLoad.street1) {
          dataToLoad.street1 = [dataToLoad.street1, dataToLoad.street2].join(' ');
        } else {
          dataToLoad.street1 = dataToLoad.street2;
        }
        dataToLoad.street2 = null;
      }
      if (dataToLoad.address_line_3) {
        dataToLoad.street1 = [dataToLoad.address_line_3, dataToLoad.street1].join('/');
        dataToLoad.address_line_3 = null;
      }
    }
    return this._super(store, primaryModelClass, dataToLoad, id, requestType);
  },
  serialize(snapshot, options) {
    const json = this._super(snapshot, options);

    /* This is done due to an expectation in roster-rails.
     * The data needs to be passed not as a JSON array,
     * but a stringified JSON array */
    json.hours = JSON.stringify(json.hours);
    json.special_hours = JSON.stringify(json.special_hours);
    if (!isEmpty(json.gst_hst_number)) {
      json.gst_hst_number = formatCanadianTaxId(json.gst_hst_number);
    }
    if (!isEmpty(json.qst_number)) {
      json.qst_number = formatCanadianPstRstQstId(json.qst_number);
    }
    delete json.abn; // Do not allow modifying the ABN field
    delete json.naf_code;
    delete json.fr_company_formation;
    delete json.fr_share_capital;
    if (!isEmpty(json.merchant_images) && snapshot.record.isNew) {
      json.merchant_images = json.merchant_images.map(result => {
        return result.image;
      });
    } else if (!snapshot.record.isNew) {
      delete json.merchant_images;
    }
    if (!isEmpty(json.pos_terminal_images) && snapshot.record.isNew) {
      json.pos_terminal_images = json.pos_terminal_images.map(result => {
        return result.image;
      });
    } else if (!snapshot.record.isNew) {
      delete json.pos_terminal_images;
    }
    if (isEmpty(json.pickup_enabled)) {
      delete json.pickup_enabled;
    }
    delete json.token;

    // ONCALL-9656
    if (snapshot.record.countryCode && snapshot.record.countryCode.toUpperCase() === 'JP') {
      delete json.street2;
    }
    return json;
  }
});